/**
 * useNoteAuditDetail Hook
 *
 * Fetches and manages single note audit detail
 * Supports both provider and admin endpoints via isAdminMode parameter
 */

import { useMemo } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';

import { useGetAdminNoteDetailQuery } from '../store/adminApi';
import { useGetNoteAuditDetailQuery } from '../store/api';
import type { AuditData, NoteAuditDetail } from '../types';

interface UseNoteAuditDetailOptions {
  /** Whether to use admin endpoint */
  isAdminMode?: boolean;
  /** Organization ID (required when isAdminMode is true) */
  organizationId?: string;
}

interface UseNoteAuditDetailResult {
  /** Note audit detail */
  audit: NoteAuditDetail | undefined;
  /** Parsed audit data (from current_revision.audit_data) */
  auditData: AuditData | null;
  /** Whether data is loading */
  isLoading: boolean;
  /** Whether a fetch is currently in flight (includes refetch) */
  isFetching: boolean;
  /** Error message if any */
  error: string | null;
  /** Refetch function */
  refetch: () => void;
}

export const useNoteAuditDetail = (
  noteId: string | undefined,
  options?: UseNoteAuditDetailOptions,
): UseNoteAuditDetailResult => {
  const isAdminMode = options?.isAdminMode ?? false;
  const organizationId = options?.organizationId;

  // Use admin endpoint if isAdminMode is true, otherwise use provider endpoint
  const adminQuery = useGetAdminNoteDetailQuery(
    isAdminMode && noteId && organizationId
      ? { noteId, organizationId }
      : skipToken,
  );

  const providerQuery = useGetNoteAuditDetailQuery(
    !isAdminMode && noteId ? { noteId } : skipToken,
  );

  // Use whichever query is active
  const { data, isLoading, isFetching, error, refetch } = isAdminMode
    ? adminQuery
    : providerQuery;

  // Parse the audit_data JSONB
  const auditData = useMemo((): AuditData | null => {
    const rawData = data?.current_revision?.audit_data;
    if (!rawData) return null;

    try {
      // The data might already be parsed or might be a string
      const parsed = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;
      return {
        rule_compliance_assessments: parsed.rule_compliance_assessments || [],
        billing_support: parsed.billing_support || {
          supports_suggested_cpt: false,
          proposed_primary_code: null,
          confidence: 0,
          strengths: [],
          concerns: [],
          missing_elements_for_code: [],
        },
        scores: parsed.scores || {
          completeness: 0,
          specificity: 0,
          compliance: 0,
          clinical_quality: 0,
        },
      };
    } catch {
      return null;
    }
  }, [data]);

  return {
    audit: data,
    auditData,
    isLoading,
    isFetching,
    error: error ? 'Failed to load note audit details' : null,
    refetch,
  };
};

export default useNoteAuditDetail;
